// src/ds/Icon.tsx — thin-line icon set (Unicons-style). Inherits currentColor.
// Single source for iconography across the new console (shell nav, cards, buttons).
// Ported verbatim from the designer prototype (v2/prototype/admin) so the console
// matches the approved visual language. One icon set, repo-wide — never inline an
// SVG path in a page. Add a name here when a new glyph is genuinely needed.
import React from "react";

const PATHS = {
  home: "M3 11l9-8 9 8M5 10v10h5v-6h4v6h5V10",
  swap: "M7 4v13M4 14l3 3 3-3M17 20V7M14 10l3-3 3 3",
  arrowUpRight: "M7 17L17 7M8 7h9v9",
  arrowDownLeft: "M17 7L7 17M7 8v9h9",
  refresh:
    "M3 12a9 9 0 0115-6.7L21 8M21 4v4h-4M21 12a9 9 0 01-15 6.7L3 16M3 20v-4h4",
  users:
    "M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M9 11a4 4 0 100-8 4 4 0 000 8M22 21v-2a4 4 0 00-3-3.9M16 3.1a4 4 0 010 7.8",
  building:
    "M4 21V5a1 1 0 011-1h9a1 1 0 011 1v16M15 9h4a1 1 0 011 1v11M8 8h2M8 12h2M8 16h2",
  shieldCheck: "M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3M9 12l2 2 4-4",
  coins:
    "M12 8c4.4 0 8-1.3 8-3s-3.6-3-8-3-8 1.3-8 3 3.6 3 8 3zM4 5v6c0 1.7 3.6 3 8 3M20 5v6M4 11v6c0 1.7 3.6 3 8 3s8-1.3 8-3v-6",
  box: "M21 8l-9-5-9 5 9 5 9-5zM3 8v8l9 5 9-5V8M12 13v8",
  chart: "M3 3v18h18M7 14l3-3 3 3 5-6",
  sliders: "M4 6h9M17 6h3M4 12h3M11 12h9M4 18h6M14 18h6M13 4v4M7 10v4M10 16v4",
  zap: "M13 2L3 14h8l-1 8 10-12h-8l1-8z",
  // Developer (the developer portal's workspaces and invitations).
  code: "M8 6l-6 6 6 6M16 6l6 6-6 6M14 4l-4 16",
  // Reviews. An unknown name silently falls back to `box`, so without this
  // the Support nav rendered a crate beside "Reviews" — a missing glyph
  // never errors, it just quietly draws the wrong thing.
  star: "M12 3.2l2.7 5.6 6.1.9-4.4 4.3 1.1 6.1-5.5-2.9-5.5 2.9 1.1-6.1-4.4-4.3 6.1-.9z",
  bank: "M3 10l9-6 9 6M4 10v8M9 10v8M15 10v8M20 10v8M3 21h18",
  settings:
    "M12 15a3 3 0 100-6 3 3 0 000 6M19.4 13a1.6 1.6 0 00.3 1.8 2 2 0 11-2.8 2.8 1.6 1.6 0 00-2.8 1.2V21a2 2 0 11-4 0 1.6 1.6 0 00-2.8-1.2 2 2 0 11-2.8-2.8A1.6 1.6 0 004.6 13 2 2 0 113 9a1.6 1.6 0 001.8-.3 2 2 0 112.8-2.8A1.6 1.6 0 009 4.6 2 2 0 1113 3a1.6 1.6 0 001.2 2.8 2 2 0 112.8 2.8A1.6 1.6 0 0019.4 11 2 2 0 1121 13z",
  headphones:
    "M4 14v-2a8 8 0 0116 0v2M4 14a2 2 0 00-2 2v2a2 2 0 002 2h1a1 1 0 001-1v-4a1 1 0 00-1-1H4zM20 14a2 2 0 012 2v2a2 2 0 01-2 2h-1a1 1 0 01-1-1v-4a1 1 0 011-1h1z",
  search: "M11 19a8 8 0 100-16 8 8 0 000 16zM21 21l-4.3-4.3",
  bell: "M18 8a6 6 0 10-12 0c0 7-3 9-3 9h18s-3-2-3-9M13.7 21a2 2 0 01-3.4 0",
  command: "M9 6a3 3 0 10-3 3h12a3 3 0 10-3-3v12a3 3 0 103-3H6a3 3 0 10-3 3",
  chevDown: "M6 9l6 6 6-6",
  // Sidebar collapse/expand. `panelLeft` is the rail affordance (a pane with its
  // nav column shaded); the chevrons point the way the sidebar will move.
  panelLeft: "M4 4h16v16H4zM10 4v16",
  chevLeft: "M15 6l-6 6 6 6",
  chevRight: "M9 6l6 6-6 6",
  arrowRight: "M5 12h14M13 6l6 6-6 6",
  check: "M5 13l4 4L19 7",
  pause: "M9 5v14M15 5v14",
  lock: "M5 11h14v10H5zM8 11V7a4 4 0 018 0v4",
  info: "M12 22a10 10 0 100-20 10 10 0 000 20M12 16v-5M12 8h.01",
  alertTriangle:
    "M10.3 3.9 2 18a2 2 0 002 3h16a2 2 0 002-3L13.7 3.9a2 2 0 00-3.4 0zM12 9v4M12 17h.01",
  // The Website domain (the marketing site). Same reason `star` is here: an
  // unknown name draws `box` in silence, so a missing glyph would have put a
  // crate beside "Website" in the sidebar with nothing failing anywhere.
  globe:
    "M12 22a10 10 0 100-20 10 10 0 000 20M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z",
} as const;

export type IconName = keyof typeof PATHS;

/**
 * Every glyph this set actually ships.
 *
 * Exported so callers that name an icon in DATA rather than in JSX — the nav
 * model, the command registry — can be asserted against the real set. `Icon`
 * falls back to `box` for an unknown name and never throws, so without a
 * membership check a typo'd or not-yet-added name is invisible: it renders,
 * it just renders the wrong thing.
 */
export const ICON_NAMES = Object.keys(PATHS) as IconName[];

export interface IconProps {
  name: IconName | string;
  size?: number;
  stroke?: number;
  style?: React.CSSProperties;
  /** decorative by default; pass false + a title via aria-label on the parent */
  "aria-hidden"?: boolean;
}

export function Icon({
  name,
  size = 18,
  stroke = 1.8,
  style,
  "aria-hidden": ariaHidden = true,
}: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={style}
      aria-hidden={ariaHidden}
      focusable={false}
    >
      <path d={PATHS[name as IconName] ?? PATHS.box} />
    </svg>
  );
}

export default Icon;
